import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(sessionStorage.getItem('_at') || null);

  // Persist token to sessionStorage (survives refreshes within the tab, cleared on tab close)
  const setToken = (t) => {
    tokenRef.current = t;
    if (t) sessionStorage.setItem('_at', t);
    else sessionStorage.removeItem('_at');
  };

  // Axios interceptors:
  //  • request → attach current access token
  //  • response → on 401 "Token expired", silently refresh via cookie and retry once
  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use((config) => {
      if (tokenRef.current) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${tokenRef.current}`;
      }
      return config;
    });

    // Single-flight refresh promise so a burst of 401s only triggers one refresh
    let refreshPromise = null;
    const doRefresh = () => {
      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${API}/auth/refresh`, {}, { withCredentials: true, _skipAuthRetry: true })
          .then((res) => {
            const newTok = res.data?.token;
            if (newTok) setToken(newTok);
            return newTok;
          })
          .finally(() => { refreshPromise = null; });
      }
      return refreshPromise;
    };

    const respInterceptor = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const url = original.url || '';
        // Only retry on 401, and never for the refresh/login/logout endpoints
        const isAuthEndpoint = /\/auth\/(refresh|login|logout|register)/.test(url);
        if (status !== 401 || original._skipAuthRetry || original._retry || isAuthEndpoint) {
          return Promise.reject(error);
        }
        original._retry = true;
        try {
          const newTok = await doRefresh();
          if (newTok) {
            original.headers = original.headers || {};
            original.headers['Authorization'] = `Bearer ${newTok}`;
          }
          return await axios(original);
        } catch (refreshErr) {
          logger.error('Silent auth refresh failed:', refreshErr);
          // Session is truly gone — surface a friendly heads-up before route guards send user to login
          toast.info("Your session expired — you'll be signed back in shortly.", {
            id: 'session-expired',
            duration: 5000,
          });
          setToken(null);
          setUser(null);
          return Promise.reject(error);
        }
      },
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(respInterceptor);
    };
  }, []);

  const checkAuth = useCallback(async () => {
    if (!tokenRef.current) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (err) {
      // Try refreshing via cookie (may work in non-Safari browsers)
      try {
        const refreshRes = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        // If refresh returned a new token, store it
        if (refreshRes.data?.token) setToken(refreshRes.data.token);
        const { data } = await axios.get(`${API}/auth/me`);
        setUser(data);
      } catch (refreshErr) {
        logger.error('Auth refresh failed:', refreshErr);
        setUser(null);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.token) setToken(data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (name, email, password, company, phone) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password, company: company || '', phone: phone || '' }, { withCredentials: true });
      if (data.token) setToken(data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) { logger.error('Logout error:', err); }
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const getWsTicket = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/auth/ws-ticket`, {});
      return data.ticket;
    } catch (err) {
      logger.error('WS ticket fetch error:', err);
      return null;
    }
  }, []);

  const contextValue = useMemo(() => ({ user, loading, login, register, logout, refreshUser, getWsTicket }), [user, loading, login, register, logout, refreshUser, getWsTicket]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
